import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Wrench, Shield, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const RoofReplacementProcessPage = () => {
  const handleCallNow = () => {
    window.location.href = 'tel:252-371-2266';
  };

  const handleGetEstimate = () => {
    window.location.href = '/contact';
  };

  return (
    <Layout>
      <Helmet>
        <title>Roof Replacement Process in NC | Carolina Trophy Roofs</title>
        <meta name="description" content="Discover the full roof replacement process from inspection to final cleanup. Expert guidance by Carolina Trophy Roofs. Free estimates. Call (252) 371-2266." />
      </Helmet>
      
      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              🏗️ The Roof Replacement Process: A Step-by-Step Guide for North Carolina Homeowners
            </h1>
          </div>

          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">🧠 Introduction: Replacing Your Roof Doesn't Have to Be Overwhelming</h2>
            <p className="text-lg text-gray-700 mb-4">
              Getting a new roof can feel intimidating. You might be asking:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li><em>Will it disrupt my daily life?</em></li>
              <li><em>What if I choose the wrong material or contractor?</em></li>
              <li><em>How much is this really going to cost me?</em></li>
            </ul>
            <p className="text-lg text-gray-700">
              At <strong>Carolina Trophy Roofs</strong>, we understand how high the stakes are when it comes to replacing your roof—especially in storm-prone Eastern North Carolina. That's why we walk you through each stage of the process with honesty, clarity, and expertise. Whether your roof was damaged by a recent storm or it's simply time to upgrade, this guide will give you the peace of mind you deserve.
            </p>
          </div>

          {/* Who Is This Guide For */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">📍 Who Is This Guide For?</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Homeowners in <strong>Rocky Mount</strong>, <strong>Greenville</strong>, or anywhere in <strong>Eastern NC</strong></li>
              <li>Anyone dealing with <strong>storm damage</strong>, <strong>insurance claims</strong>, or <strong>aging roofing</strong></li>
              <li>Realtors, home sellers, or buyers needing <strong>roof certifications</strong> or <strong>replacement quotes</strong></li>
            </ul>
          </div>

          {/* Process Steps */}
          <div className="space-y-8 mb-12">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl flex items-center">
                  <Wrench className="h-6 w-6 mr-2" />
                  Step 1: Roof Inspection & Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Every roof replacement begins with a professional inspection.</p>
                
                <h4 className="font-semibold mb-2">What We Look For:</h4>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Missing, curling, or cracked shingles</li>
                  <li>Signs of rot, sagging, or water intrusion</li>
                  <li>Storm-related damage (hail, wind uplift, punctures)</li>
                  <li>Attic ventilation & insulation problems</li>
                </ul>

                <h4 className="font-semibold mb-2">Tools We Use:</h4>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Drones for aerial footage</li>
                  <li>Moisture meters for leak detection</li>
                  <li>Structural scans for decking health</li>
                </ul>

                <div className="mt-4 p-4 bg-brand-carolina-blue/10 rounded-lg">
                  <p className="text-brand-carolina-blue font-medium">
                    📞 Schedule a free inspection now: (252) 371-2266
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">📝 Step 2: Quote, Material Selection & Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  After assessing your roof's condition, we provide a clear, itemized quote—including all labor, materials, disposal, and warranty details.
                </p>
                
                <h4 className="font-semibold mb-2">You'll Choose:</h4>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li><strong>Material Type</strong>: asphalt, metal, synthetic, etc.</li>
                  <li><strong>Color</strong> and <strong>style</strong> to match your home</li>
                  <li><strong>Warranty Level</strong> (standard or extended)</li>
                </ul>

                <p className="mb-4">
                  We also explain <strong>financing options</strong> and can coordinate directly with your <strong>insurance provider</strong> if applicable.
                </p>

                <div className="mt-4 p-4 bg-brand-trophy-gold/10 rounded-lg">
                  <p className="text-sm">
                    🎯 <strong>Pro Tip:</strong> Ask about our architectural shingles with 130+ mph wind ratings—ideal for NC's storm season.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🧰 Step 3: Permitting & Prep</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Before any shingles come off, we take care of:</p>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Local permitting</li>
                  <li>HOA approvals (if applicable)</li>
                  <li>Ordering materials</li>
                  <li>Scheduling the install date with you</li>
                </ul>

                <p className="mb-2 font-semibold">We also set expectations:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Start and end dates</li>
                  <li>Daily work hours</li>
                  <li>What access we'll need (driveway, power, etc.)</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🔨 Step 4: Roof Tear-Off & Inspection of Decking</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Now the real work begins.</p>
                
                <h4 className="font-semibold mb-2">What Happens:</h4>
                <ol className="list-decimal list-inside space-y-1 mb-4">
                  <li>Old shingles and underlayment are carefully removed.</li>
                  <li>Decking (the wood base of your roof) is inspected for rot or damage.</li>
                  <li>Any compromised wood is replaced to ensure structural integrity.</li>
                </ol>

                <p>We protect your landscaping and property with tarps and magnetic nail rollers during cleanup.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🧱 Step 5: New Roofing Installation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">Once your roof is stripped and prepped, it's time to rebuild—layer by layer:</p>
                
                <h4 className="font-semibold mb-2">Typical Installation Steps:</h4>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Ice and water shield (especially in valleys and around vents)</li>
                  <li>Synthetic underlayment</li>
                  <li>Drip edge, flashing, pipe boots</li>
                  <li>Ventilation systems (ridge vents or fans)</li>
                  <li>Shingle or panel installation</li>
                  <li>Final sealing and detail work</li>
                </ul>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm">
                    🧠 <strong>Fun Fact:</strong> Proper ventilation can extend your roof life by 30% and lower attic temps by 20–40°F.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🧹 Step 6: Final Cleanup & Walkthrough</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">We never leave a mess behind. After install, we:</p>
                <ul className="list-disc list-inside space-y-1 mb-4">
                  <li>Clean gutters and ground areas</li>
                  <li>Roll for nails and debris</li>
                  <li>Walk through your new roof with you</li>
                  <li>Review warranties and care tips</li>
                </ul>

                <p className="mb-2 font-semibold">You'll receive a project completion folder with:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Photos of your new roof</li>
                  <li>Product registration info</li>
                  <li>Warranty documents</li>
                  <li>Optional maintenance plan offers</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Timeline Table */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center">
              <Clock className="h-6 w-6 mr-2" />
              How Long Does It Take?
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left">Roof Size (Sq Ft)</th>
                    <th className="border border-gray-300 p-3 text-left">Typical Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">Under 2,000</td>
                    <td className="border border-gray-300 p-3">1 day</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 p-3">2,000–4,000</td>
                    <td className="border border-gray-300 p-3">1–2 days</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 p-3">Over 4,000</td>
                    <td className="border border-gray-300 p-3">2–3+ days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Weather delays and material complexity may affect timeline—but we <strong>always communicate clearly.</strong>
            </p>
          </div>

          {/* Why Choose Us */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <Shield className="h-6 w-6 mr-2" />
              Why Choose Carolina Trophy Roofs?
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>🏆 GAF Certified Roofing Contractor</li>
              <li>💬 5-Star Reviewed by Homeowners in Rocky Mount & Greenville</li>
              <li>🌪️ Storm Damage Specialists</li>
              <li>📝 Insurance Claim Support</li>
              <li>🔧 Extended Warranty Packages</li>
              <li>📞 Local + Fast: <strong>Call us today at (252) 371-2266</strong></li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">🧾 Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">How do I know it's time to replace my roof?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Look for signs like missing shingles, granule loss, interior leaks, or a roof over 20 years old.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Will my homeowners insurance cover roof replacement?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>If the damage was caused by a covered peril (hail, wind, falling debris), yes. We can help file and manage your claim.</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Do you offer financing?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>Yes! We work with trusted lenders to offer monthly payments that work with your budget.</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* High DA Citations Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Sources & Further Reading</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>
                  1. National Roofing Contractors Association - <a href="https://www.nrca.net/consumer/selecting-roofing-professional" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Selecting a Roofing Professional</a>
                </p>
                <p>
                  2. Consumer Reports - <a href="https://www.consumerreports.org/roofing/how-to-hire-a-roofing-contractor/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">How to Hire a Roofing Contractor</a>
                </p>
                <p>
                  3. Better Business Bureau - <a href="https://www.bbb.org/all/choose-contractor" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">How to Choose a Contractor</a>
                </p>
                <p>
                  4. Federal Trade Commission - <a href="https://www.consumer.ftc.gov/articles/0242-hiring-contractor" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Hiring a Contractor</a>
                </p>
                <p>
                  5. FEMA - <a href="https://www.fema.gov/emergency-managers/risk-management/building-science/bsc-bulletins/high-wind-residential-construction" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">High Wind Resistant Construction</a>
                </p>
                <p>
                  6. HomeAdvisor - <a href="https://www.homeadvisor.com/cost/roofing/install-roof/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Roof Replacement Cost Guide</a>
                </p>
                <p>
                  7. Angie's List - <a href="https://www.angieslist.com/articles/how-get-roof-replacement-quotes.htm" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">How to Get Roof Replacement Quotes</a>
                </p>
                <p>
                  8. This Old House - <a href="https://www.thisoldhouse.com/roofing/21018465/how-to-choose-roofing-contractor" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">How to Choose a Roofing Contractor</a>
                </p>
                <p>
                  9. Insurance Information Institute - <a href="https://www.iii.org/article/tips-filing-homeowners-insurance-claim" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Filing Homeowners Insurance Claims</a>
                </p>
                <p>
                  10. GAF - <a href="https://www.gaf.com/en-us/roofing-guide/roof-replacement-process" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Roof Replacement Process Guide</a>
                </p>
              </div>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>Disclaimer:</strong> This information is for educational purposes. Always consult with licensed roofing professionals and your insurance provider for specific advice about your property and coverage.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Call to Action */}
          <div className="text-center bg-brand-carolina-blue text-white p-8 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">📞 Ready for a Free Inspection?</h2>
            <p className="text-lg mb-6">
              Replacing your roof doesn't have to feel like a burden. With Carolina Trophy Roofs, it's a transparent, predictable, and empowering experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleCallNow}
                className="bg-brand-safety-orange text-white hover:bg-brand-safety-orange/90"
              >
                <Phone className="h-5 w-5 mr-2" />
                Call (252) 371-2266
              </Button>
              <Button 
                onClick={handleGetEstimate}
                variant="outline"
                className="bg-white text-brand-carolina-blue hover:bg-gray-100"
              >
                Request Free Quote
              </Button>
            </div>
          </div>

          {/* Final Word */}
          <div className="mt-12 text-center">
            <h2 className="text-2xl font-bold mb-4">🎯 Final Word</h2>
            <p className="text-lg text-gray-700 mb-4">
              Whether your roof is failing, storm-damaged, or simply due for an upgrade, knowing the process gives you power. Carolina Trophy Roofs is here to guide you every step of the way—with quality you can see and trust you can feel.
            </p>
            <p className="text-xl font-bold text-brand-carolina-blue">
              ☎️ Call today: (252) 371-2266
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RoofReplacementProcessPage;
